"use client";
/**
 * Zwaluw — Block 03+04 · Header (desktop nav + mobile drawer + sticky CTA)
 *
 * Combineert 3 layout-elementen:
 *   - Desktop sticky header met logo, mega-menus (Welke oplossing? / Collectie)
 *     en 2 CTA-knoppen
 *   - Mobile drawer (slide-in van rechts) met accordion-secties
 *   - Sticky mobile CTA bar onderaan (alleen mobiel)
 *
 * State: open/dicht-status van de drawer + welke accordion-secties open zijn.
 * Bron: handoff "03 Header.html" + "04 Mobile drawer.html".
 *
 * Logo: Next/Image om Vercel's image-optimization edge-cache te triggeren.
 * Anders gaat élke pageview direct naar supabase.co/storage = grote egress.
 */
import * as React from "react";
import Image from "next/image";

const ChevronDown = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ArrowRight = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const BurgerSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <line x1="4" y1="7" x2="20" y2="7" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="17" x2="20" y2="17" />
  </svg>
);

const CloseSvg = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="6" y1="18" x2="18" y2="6" />
  </svg>
);

const PhoneSvgDrawer = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export interface MegaCard {
  title: string;
  subtitle: string;
  href: string;
  /** CSS-class voor achtergrond (matcht in styles.css), bv "t-douche", "c-premium" */
  imageClass: string;
  /** SVG-string voor het icoontje (rendered binnen mega-card-icon span) */
  iconSvg?: React.ReactNode;
  /** Optioneel label-tag rechtsboven (bv "Topkeuze", "Nieuw") */
  tag?: string;
}

export interface MegaLinkColumn {
  /** Optionele kolomtitel (bv. "Over ons", "Service") */
  heading?: string;
  links: Array<{ label: string; href: string }>;
}

export interface MegaInspirationCard {
  /** URL van de achtergrond-image */
  image: string;
  /** Klein label boven de titel */
  eyebrow?: string;
  /** Hoofd-titel — mag <em> bevatten via titleEm */
  title: string;
  titleEm?: string;
  /** Sub-tekst onder de titel */
  sub?: string;
  /** CTA-knop onderaan */
  cta?: { label: string; href: string };
}

export interface MegaMenu {
  /** Layout 1 — Cards in een grid (default 4 columns), zoals "Welke
   *  oplossing?" en "Collectie" */
  cards?: MegaCard[];
  /** Layout 2 — 1 of 2 link-kolommen + 1 grote inspiratie-image rechts.
   *  Voor secundaire nav zoals "Over Zwaluw" met meer text-zware items.
   *  Wanneer aanwezig wordt deze layout gerenderd ipv `cards`. */
  linkColumns?: MegaLinkColumn[];
  inspiration?: MegaInspirationCard;
  /** Optioneel — extra rij van tekst-links onder de cards. Voor SEO-discovery
   *  van secundaire pagina's zonder de mega-grid visueel te vol te maken. */
  secondaryLinks?: { label: string; links: Array<{ label: string; href: string }> };
  /** Footer-link onderin (links) */
  footerLink?: { label: string; href: string };
  /** Footer-tekst rechts (bv "Twijfelt u? Bel..."); kan HTML bevatten */
  footerSide?: React.ReactNode;
}

export interface NavItem {
  label: string;
  href?: string;
  /** Wanneer aanwezig → mega-menu dropdown (visuele cards met icons) */
  mega?: MegaMenu;
  /** Simpele dropdown — flat list van links, geen cards. Voor secundaire
   *  navigatie zoals "Over Zwaluw" met linkjes naar Werkwijze, Toonzalen, etc. */
  dropdown?: Array<{ label: string; href: string }>;
}

export interface MobileSection {
  label: string;
  links: Array<{ label: string; href: string }>;
  /** Secundaire links (bv. SEO-niches "Ook in") — gerenderd als chips
   *  onder de primaire links met aparte label-header. */
  secondaryLabel?: string;
  secondaryLinks?: Array<{ label: string; href: string }>;
}

export interface HeaderProps {
  logoSrc: string;
  logoAlt?: string;
  homeHref?: string;
  /** Desktop nav-items */
  navItems: NavItem[];
  /** Mobile-drawer accordion (separately want desktop heeft mega-cards, mobile flat list) */
  mobileSections?: MobileSection[];
  /** Mobile drawer simpele links onderaan accordion */
  mobileLinks?: Array<{ label: string; href: string }>;
  /** Phone-nummer in mobile drawer */
  mobilePhone?: { display: string; href: string };
  /** Primaire CTA (default: "Gratis brochure") */
  ctaPrimary?: { label: string; href: string };
  /** Secundaire CTA (default: "Thuisadvies", ghost-stijl) */
  ctaSecondary?: { label: string; href: string };
  /** Sticky mobile CTA-bar onderaan tonen (default true) */
  showStickyMobileCta?: boolean;
  /** Toon een klein telefoon-icoon naast de hamburger in de mobile header.
   *  Direct-bellen zonder eerst drawer te openen. Default false (alleen
   *  ingeschakeld voor specifieke brands die conversie via tel willen pushen). */
  showHeaderPhoneMobile?: boolean;
}

const ZwBtn: React.FC<{ variant: "primary" | "ghost"; children: React.ReactNode; href?: string; onClick?: () => void; className?: string }> = ({
  variant,
  children,
  href,
  onClick,
  className,
}) => {
  const cls = `zw-btn zw-btn-${variant}${className ? ` ${className}` : ""}`;
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {children}
    </button>
  );
};

export function Header({
  logoSrc,
  logoAlt = "Zwaluw Comfortsanitair",
  homeHref = "/",
  navItems,
  mobileSections,
  mobileLinks,
  mobilePhone,
  ctaPrimary = { label: "Gratis brochure", href: "/gratis-brochure" },
  ctaSecondary = { label: "Thuisadvies", href: "/thuisadvies" },
  showStickyMobileCta = true,
  showHeaderPhoneMobile = false,
}: HeaderProps) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [openSections, setOpenSections] = React.useState<Set<number>>(new Set());

  const toggleSection = (i: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  // Body scroll-lock wanneer drawer open is
  React.useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <>
      {/* Desktop header */}
      <div className="ha">
        <a href={homeHref} className="zw-logo">
          <Image
            src={logoSrc}
            alt={logoAlt}
            width={212}
            height={48}
            style={{ height: 48, width: "auto" }}
            priority
            unoptimized={logoSrc.endsWith(".svg")}
          />
        </a>
        <nav className="ha-nav">
          {navItems.map((item, i) => {
            if (item.dropdown && item.dropdown.length > 0) {
              return (
                <div key={i} className="ha-item">
                  <button className="ha-trigger" type="button">
                    {item.label} {ChevronDown}
                  </button>
                  <div className="ha-dropdown">
                    {item.dropdown.map((link, j) => (
                      <a key={j} href={link.href}>
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              );
            }
            if (!item.mega) {
              return (
                <a key={i} href={item.href ?? "#"}>
                  {item.label}
                </a>
              );
            }
            // Layout 2 — link-kolommen + inspiratie-image
            if (item.mega.linkColumns && item.mega.linkColumns.length > 0) {
              return (
                <div key={i} className="ha-item">
                  <button className="ha-trigger" type="button">
                    {item.label} {ChevronDown}
                  </button>
                  <div className="mega mega-over">
                    <div className="mega-over-grid">
                      <div className="mega-over-cols">
                        {item.mega.linkColumns.map((col, j) => (
                          <div key={j} className="mega-over-col">
                            {col.heading ? <div className="mega-over-heading">{col.heading}</div> : null}
                            <ul>
                              {col.links.map((l, k) => (
                                <li key={k}>
                                  <a href={l.href}>{l.label}</a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {item.mega.inspiration ? (
                        <a className="mega-over-card" href={item.mega.inspiration.cta?.href ?? "#"}>
                          <div
                            className="mega-over-card-img"
                            style={{ backgroundImage: `linear-gradient(rgba(31,38,36,.25), rgba(31,38,36,.55)), url('${item.mega.inspiration.image}')` }}
                          />
                          <div className="mega-over-card-content">
                            {item.mega.inspiration.eyebrow ? (
                              <div className="mega-over-card-eyebrow">{item.mega.inspiration.eyebrow}</div>
                            ) : null}
                            <div className="mega-over-card-title">
                              {item.mega.inspiration.title}
                              {item.mega.inspiration.titleEm ? (
                                <>
                                  {" "}
                                  <em>{item.mega.inspiration.titleEm}</em>
                                </>
                              ) : null}
                            </div>
                            {item.mega.inspiration.sub ? (
                              <div className="mega-over-card-sub">{item.mega.inspiration.sub}</div>
                            ) : null}
                            {item.mega.inspiration.cta ? (
                              <span className="mega-over-card-cta">
                                {item.mega.inspiration.cta.label} {ArrowRight}
                              </span>
                            ) : null}
                          </div>
                        </a>
                      ) : null}
                    </div>
                    {(item.mega.footerLink || item.mega.footerSide) && (
                      <div className="mega-foot">
                        {item.mega.footerLink ? (
                          <a className="mega-foot-link" href={item.mega.footerLink.href}>
                            {item.mega.footerLink.label} {ArrowRight}
                          </a>
                        ) : <span />}
                        {item.mega.footerSide ? <span className="mega-foot-side">{item.mega.footerSide}</span> : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            // Layout 1 — Cards (default)
            return (
              <div key={i} className="ha-item">
                <button className="ha-trigger" type="button">
                  {item.label} {ChevronDown}
                </button>
                <div className="mega">
                  <div className="mega-grid">
                    {(item.mega.cards ?? []).map((card, j) => (
                      <a key={j} className="mega-card" href={card.href}>
                        <div className={`mega-card-img ${card.imageClass}`}>
                          {card.tag ? <span className="mega-card-tag">{card.tag}</span> : null}
                          {card.iconSvg ? <span className="mega-card-icon">{card.iconSvg}</span> : null}
                        </div>
                        <div className="mega-card-title">{card.title}</div>
                        <div className="mega-card-sub">{card.subtitle}</div>
                      </a>
                    ))}
                  </div>
                  {item.mega.secondaryLinks && item.mega.secondaryLinks.links.length > 0 ? (
                    <div className="mega-secondary">
                      <span className="mega-secondary-label">{item.mega.secondaryLinks.label}</span>
                      <span className="mega-secondary-links">
                        {item.mega.secondaryLinks.links.map((l, j) => (
                          <a key={j} href={l.href}>{l.label}</a>
                        ))}
                      </span>
                    </div>
                  ) : null}
                  {(item.mega.footerLink || item.mega.footerSide) && (
                    <div className="mega-foot">
                      {item.mega.footerLink ? (
                        <a className="mega-foot-link" href={item.mega.footerLink.href}>
                          {item.mega.footerLink.label} {ArrowRight}
                        </a>
                      ) : <span />}
                      {item.mega.footerSide ? <span className="mega-foot-side">{item.mega.footerSide}</span> : null}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="ha-cta">
          <ZwBtn variant="ghost" href={ctaSecondary.href}>
            {ctaSecondary.label}
          </ZwBtn>
          <ZwBtn variant="primary" href={ctaPrimary.href}>
            {ctaPrimary.label}
          </ZwBtn>
        </div>
        {showHeaderPhoneMobile && mobilePhone ? (
          <a
            className="ha-mobile-phone"
            href={`tel:${mobilePhone.href}`}
            aria-label={`Bel ${mobilePhone.display}`}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </a>
        ) : null}
        <button className="ha-burger" type="button" aria-label="Menu openen" onClick={() => setDrawerOpen(true)}>
          {BurgerSvg}
          <span className="ha-burger-label">Menu</span>
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`mobile-drawer-overlay${drawerOpen ? " open" : ""}`} onClick={() => setDrawerOpen(false)} />
      <aside className={`mobile-drawer${drawerOpen ? " open" : ""}`}>
        <div className="mobile-drawer-head">
          <Image
            src={logoSrc}
            alt={logoAlt}
            width={141}
            height={32}
            style={{ height: 32, width: "auto" }}
            unoptimized={logoSrc.endsWith(".svg")}
          />
          <button
            type="button"
            className="mobile-drawer-close"
            aria-label="Menu sluiten"
            onClick={() => setDrawerOpen(false)}
          >
            {CloseSvg}
          </button>
        </div>
        <nav className="mobile-drawer-nav">
          {(mobileSections ?? []).map((sec, i) => (
            <div key={i} className={`md-section${openSections.has(i) ? " open" : ""}`}>
              <button type="button" className="md-section-head" onClick={() => toggleSection(i)}>
                {sec.label} {ChevronDown}
              </button>
              <div className="md-section-body">
                {sec.links.map((l, j) => (
                  <a key={j} href={l.href} onClick={() => setDrawerOpen(false)}>
                    {l.label}
                  </a>
                ))}
                {sec.secondaryLinks && sec.secondaryLinks.length > 0 ? (
                  <div className="md-section-secondary">
                    {sec.secondaryLabel ? (
                      <span className="md-secondary-label">{sec.secondaryLabel}</span>
                    ) : null}
                    <div className="md-secondary-chips">
                      {sec.secondaryLinks.map((l, j) => (
                        <a
                          key={j}
                          href={l.href}
                          onClick={() => setDrawerOpen(false)}
                          className="md-chip"
                        >
                          {l.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          {(mobileLinks ?? []).map((l, i) => (
            <a key={i} href={l.href} onClick={() => setDrawerOpen(false)}>
              {l.label}
            </a>
          ))}
        </nav>
        <div className="mobile-drawer-cta">
          {mobilePhone ? (
            <a href={`tel:${mobilePhone.href}`} className="mobile-drawer-cta-phone">
              {PhoneSvgDrawer} Bel: {mobilePhone.display}
            </a>
          ) : (
            <ZwBtn variant="ghost" href={ctaSecondary.href}>
              {ctaSecondary.label}
            </ZwBtn>
          )}
          <ZwBtn variant="primary" href={ctaPrimary.href}>
            {ctaPrimary.label}
          </ZwBtn>
        </div>
      </aside>

      {/* Sticky mobile CTA */}
      {showStickyMobileCta ? (
        <div className="mobile-cta-fixed">
          <ZwBtn variant="ghost" href={ctaSecondary.href}>
            {ctaSecondary.label}
          </ZwBtn>
          <ZwBtn variant="primary" href={ctaPrimary.href}>
            Brochure
          </ZwBtn>
        </div>
      ) : null}
    </>
  );
}

export default Header;
